import { supabase } from './supabase-client';

export interface JobInvitation {
  id: string;
  job_posting_id: string;
  candidate_email: string;
  status: 'pending' | 'accepted' | 'completed';
  invited_by: string;
  expires_at: string | null;
  created_at: string;
  accepted_at: string | null;
  completed_at: string | null;
  invitation_token?: string;
}

export async function checkCandidateHasAccess(
  jobId: string,
  userEmail: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('check_candidate_has_access', {
      p_job_id: jobId,
      p_user_email: userEmail,
    });

    if (error) {
      console.error('Error checking candidate access:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    console.error('Error checking candidate access:', error);
    return false;
  }
}

export async function createInvitations(
  jobId: string,
  emails: string[],
  invitedBy: string
): Promise<{ success: number; failed: number; errors: string[] }> {
  const results = { success: 0, failed: 0, errors: [] as string[] };

  for (const email of emails) {
    try {
      const { error } = await supabase.from('job_invitations').insert({
        job_posting_id: jobId,
        candidate_email: email.toLowerCase().trim(),
        invited_by: invitedBy,
        status: 'pending',
      });

      if (error) {
        if (error.code === '23505') {
          results.errors.push(`${email}: Already invited`);
        } else {
          results.errors.push(`${email}: ${error.message}`);
        }
        results.failed++;
      } else {
        results.success++;
      }
    } catch (error) {
      results.errors.push(`${email}: Unexpected error`);
      results.failed++;
    }
  }

  return results;
}

export async function getJobInvitations(
  jobId: string
): Promise<JobInvitation[]> {
  const { data, error } = await supabase
    .from('job_invitations')
    .select('*')
    .eq('job_posting_id', jobId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching invitations:', error);
    return [];
  }

  return data || [];
}

export async function getUserInvitations(
  userEmail: string
): Promise<JobInvitation[]> {
  const { data, error } = await supabase
    .from('job_invitations')
    .select('*')
    .eq('candidate_email', userEmail)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching user invitations:', error);
    return [];
  }

  return data || [];
}

export async function updateInvitationStatus(
  invitationId: string,
  status: 'pending' | 'accepted' | 'completed',
  timestamp?: string
): Promise<boolean> {
  const updateData: any = { status };

  if (status === 'accepted') {
    updateData.accepted_at = timestamp || new Date().toISOString();
  } else if (status === 'completed') {
    updateData.completed_at = timestamp || new Date().toISOString();
  }

  const { error } = await supabase
    .from('job_invitations')
    .update(updateData)
    .eq('id', invitationId);

  if (error) {
    console.error('Error updating invitation status:', error);
    return false;
  }

  return true;
}

export async function deleteInvitation(invitationId: string): Promise<boolean> {
  const { error } = await supabase
    .from('job_invitations')
    .delete()
    .eq('id', invitationId);

  if (error) {
    console.error('Error deleting invitation:', error);
    return false;
  }

  return true;
}

export async function markInvitationAsAccepted(
  jobId: string,
  userEmail: string
): Promise<void> {
  const { error } = await supabase
    .from('job_invitations')
    .update({
      status: 'accepted',
      accepted_at: new Date().toISOString(),
    })
    .eq('job_posting_id', jobId)
    .eq('candidate_email', userEmail)
    .eq('status', 'pending');

  if (error) {
    console.error('Error marking invitation as accepted:', error);
  }
}

export function parseEmailList(input: string): string[] {
  const emails = input
    .split(/[\n,;]/)
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0 && email.includes('@'));

  return [...new Set(emails)];
}

export async function validateInvitationToken(
  jobId: string,
  token: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('job_invitations')
      .select('id, job_posting_id')
      .eq('job_posting_id', jobId)
      .eq('invitation_token', token)
      .maybeSingle();

    if (error) {
      console.error('Error validating invitation token:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error validating invitation token:', error);
    return false;
  }
}

export async function getInvitationByToken(
  token: string
): Promise<JobInvitation | null> {
  try {
    const { data, error } = await supabase
      .from('job_invitations')
      .select('*')
      .eq('invitation_token', token)
      .maybeSingle();

    if (error) {
      console.error('Error getting invitation by token:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error getting invitation by token:', error);
    return null;
  }
}

export async function linkUserToInvitation(
  token: string,
  userEmail: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('job_invitations')
      .update({
        candidate_email: userEmail.toLowerCase().trim(),
      })
      .eq('invitation_token', token)
      .is('candidate_email', null);

    if (error) {
      console.error('Error linking user to invitation:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error linking user to invitation:', error);
    return false;
  }
}

export async function getJobInvitationToken(
  jobId: string
): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('job_invitations')
      .select('invitation_token')
      .eq('job_posting_id', jobId)
      .is('candidate_email', null)
      .maybeSingle();

    if (error) {
      console.error('Error getting job invitation token:', error);
      return null;
    }

    return data?.invitation_token || null;
  } catch (error) {
    console.error('Error getting job invitation token:', error);
    return null;
  }
}

export async function createOrGetInvitationToken(
  jobId: string,
  invitedBy: string
): Promise<string | null> {
  try {
    const existingToken = await getJobInvitationToken(jobId);
    if (existingToken) {
      return existingToken;
    }

    const { data, error } = await supabase
      .from('job_invitations')
      .insert({
        job_posting_id: jobId,
        candidate_email: null,
        invited_by: invitedBy,
        status: 'pending',
      })
      .select('invitation_token')
      .single();

    if (error) {
      console.error('Error creating invitation token:', error);
      return null;
    }

    return data?.invitation_token || null;
  } catch (error) {
    console.error('Error creating invitation token:', error);
    return null;
  }
}
